import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Grid,
    TextField,
    MenuItem,
    CircularProgress
} from "@mui/material";
import { useForm, Controller } from "react-hook-form";
import {
    DetalharEspecificacoesTecnicas,
    IDetalheProdutoEstoque
} from "../../../../../../../types/api/estoque/detalhar-produto-estoque";
import axiosClient from "../../../../../../../lib/axios";
import { useMutation, useQueryClient } from "react-query";
import { useSnackbar } from "notistack";
import { EditarEspecificacoesTecnicasPayload } from "../../../../../../../types/mutations/estoque/editar-produtos";
import SelectUnidadeMedida from "../../../../../../../components/SelectsSimpleApiQuery/SelectUnidadeMedida";

type Props = {
    open: boolean;
    onClose: () => void;
    produto: IDetalheProdutoEstoque;
};

export default function DialogEditEspecificacoesTecnicas({ open, onClose, produto }: Props) {
    const { handleSubmit, control, formState: { errors } } = useForm<DetalharEspecificacoesTecnicas>({
        defaultValues: {
            tipo_produto: produto.tipo_produto ?? "-",
            unidade_medida: produto.unidade_medida ?? null,
            aceita_decimal: produto.aceita_decimal ?? "-",
            unidade_padrao_etiqueta: produto.unidade_padrao_etiqueta ?? null,
            quantidade_padrao_etiqueta: produto.quantidade_padrao_etiqueta ?? "-",
            unidade_fator_conversao: produto.unidade_fator_conversao ?? null,
            quantidade_fator_conversao: produto.quantidade_fator_conversao ?? "-",
        }
    });

    const queryClient = useQueryClient();
    const { enqueueSnackbar } = useSnackbar();

    const { mutate, isLoading } = useMutation({
        mutationKey: ['editar-produto', produto.id],
        mutationFn: (payload: EditarEspecificacoesTecnicasPayload) =>
            axiosClient.put(`/estoque/produtos/editar-produto/${produto.id}`, payload),
        onSuccess: () => {
            enqueueSnackbar("Produto alterado com sucesso!", { variant: "success" });
            queryClient.invalidateQueries("detalhe-produto");
            onClose();
        },
        onError: (error: any) => {
            enqueueSnackbar(error?.response?.data?.message || "Erro ao editar produto!", { variant: "error" });
        }
    });

    const onSubmit = (values: DetalharEspecificacoesTecnicas) => {
        mutate({
            ...values,
            unidade_medida:values.unidade_medida?.sigla,
            unidade_padrao_etiqueta:values.unidade_padrao_etiqueta?.sigla,
            unidade_fator_conversao:values.unidade_fator_conversao?.sigla,
        });
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>Editar Especificações Técnicas</DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} py={1}>
                        <Grid size={{xs: 12, md: 4}}>
                            <Controller
                                control={control}
                                name="tipo_produto"
                                rules={{ required: "Tipo é obrigatório" }}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        select
                                        label="Tipo"
                                        fullWidth
                                        required
                                        error={!!errors.tipo_produto}
                                        helperText={errors.tipo_produto?.message}
                                    >
                                        <MenuItem value="P">Produto</MenuItem>
                                        <MenuItem value="S">Serviço</MenuItem>
                                        <MenuItem value="I">Ativo Imobilizado</MenuItem>
                                        <MenuItem value="C">Consumo</MenuItem>
                                        <MenuItem value="T">Produto Terceiro</MenuItem>
                                    </TextField>
                                )}
                            />
                        </Grid>
                        <Grid size={{xs: 12, md: 4}}>
                        <Controller
                            control={control}
                            name="unidade_medida"
                            rules={{ required: "Unidade é obrigatória" }}
                            render={({ field }) => (
                                <SelectUnidadeMedida
                                    value={Array.isArray(field.value) ? field.value : field.value ? [field.value] : null}
                                    onChange={field.onChange}
                                    error={!!errors.unidade_medida}
                                    multiple={false}
                                />
                            )}
                        />
                    </Grid>
                        <Grid size={{xs: 12, md: 4}}>
                            <Controller
                                name="aceita_decimal"
                                control={control}
                                render={({ field }) => (
                                    <TextField
                                        select
                                        label="Permite Decimais?"
                                        fullWidth
                                        {...field}
                                        value={field.value ?? ""}
                                        error={!!errors.aceita_decimal}
                                        helperText={errors.aceita_decimal?.message}
                                    >
                                        <MenuItem value="S">Sim</MenuItem>
                                        <MenuItem value="N">Não</MenuItem>
                                    </TextField>
                                )}
                            />
                        </Grid>
                        <Grid size={{xs: 12, md: 6}}>
                            <Controller
                                name="unidade_padrao_etiqueta"
                                control={control}
                                render={({ field }) => (
                                    <SelectUnidadeMedida
                                        label="Unidade padrão etiqueta"
                                        value={Array.isArray(field.value) ? field.value : field.value ? [field.value] : null}
                                        onChange={field.onChange}
                                        error={!!errors.unidade_medida}
                                        multiple={false}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid size={{xs: 12, md: 6}}>
                            <Controller
                                name="quantidade_padrao_etiqueta"
                                control={control}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        label="Quant. Unid. Padrão"
                                        fullWidth
                                        error={!!errors.quantidade_padrao_etiqueta}
                                        helperText={errors.quantidade_padrao_etiqueta?.message}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid size={{xs: 12, md: 6}}>
                            <Controller
                                name="unidade_fator_conversao"
                                control={control}
                                render={({ field }) => (
                                    <SelectUnidadeMedida
                                        label="Unidade fator conversão"
                                        value={Array.isArray(field.value) ? field.value : field.value ? [field.value] : null}
                                        onChange={field.onChange}
                                        error={!!errors.unidade_medida}
                                        multiple={false}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid size={{xs: 12, md: 6}}>
                            <Controller
                                name="quantidade_fator_conversao"
                                control={control}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        label="Quant. Fator Conversão"
                                        fullWidth
                                        error={!!errors.quantidade_fator_conversao}
                                        helperText={errors.quantidade_fator_conversao?.message}
                                    />
                                )}
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={onClose} disabled={isLoading}>Cancelar</Button>
                    <Button
                        variant="contained"
                        color="primary"
                        disabled={isLoading}
                        onClick={handleSubmit(onSubmit)}
                    >
                        {isLoading && (
                            <CircularProgress size={15} sx={{ marginRight: 1 }} />
                        )}
                        Salvar
                    </Button>
                </DialogActions>
        </Dialog>
    );
}